import { formatCurrency } from "./index"; // Adjust path to your currency helper

/**
 * Enterprise WhatsApp Engine for iShopMaster
 * Logic: Uses structured lists and specific indentation instead of ASCII grids 
 * to ensure perfect layout on all mobile devices.
 */
export const WhatsAppFormatter = {

    // --- PRIVATE HELPERS ---
    _getDivider: () => "━━━━━━━━━━━━━━━━━━",
    _getSmallDivider: () => "--------------------------------",
    _getFooter: (company) => {
        let footer = `\n*Follow us on:*\n`;
        const fb = company?.company_fb_link || company?.companyFb;
        const ig = company?.company_instagram_link || company?.companyIg;
        const tt = company?.company_tiktok_link || company?.companyTiktok;

        if (fb) footer += `- FB: ${fb}\n`;
        if (ig) footer += `- IG: ${ig}\n`;
        if (tt) footer += `- TT: ${tt}\n`;

        footer += `\n*iShopMaster* | _Enterprise Solutions_ 🚀\n`;
        footer += `_JJSoft Global Sri Lanka_`;
        return footer;
    },

    /**
     * 1. SALES INVOICE (Bill)
     * Handles: Products, Service/Repairs, and Associated Parts
     */
    generateInvoice: (invoice, company) => {
        const customerName = `${invoice?.customer?.first_name || "CASH"} ${invoice?.customer?.last_name || ""}`;
        const companyName = company?.company_name || company?.companyName || "Our Shop";

        let message = `*${companyName.toUpperCase()}*\n`;
        message += `${company?.address || ''}\n`;
        message += `Tel: ${company?.phone_number || company?.phone || ''}\n\n`;

        message += `*🧾 SALES INVOICE*\n`;
        message += `Ref: \`\`\`${invoice?.invoice_id || invoice?.invoiceNo || 'N/A'}\`\`\`\n`;
        message += `Date: ${new Date(invoice?.invoice_date || Date.now()).toLocaleDateString()}\n`;
        message += `Cust: ${customerName.trim()}\n`;
        message += `${WhatsAppFormatter._getSmallDivider()}\n\n`;

        // A. Handle Service Items (Repairs)
        if (invoice?.invoice_type === "Service" && invoice?.serviceItems?.length > 0) {
            message += `🛠️ *SERVICE & REPAIRS*\n`;
            invoice.serviceItems.forEach((item, idx) => {
                message += `${idx + 1}. *${item.name}* (${item.model_name || 'Generic'})\n`;
                message += `   Price: ${formatCurrency(item.total)}\n`;
                if (item.associatedParts?.length > 0) {
                    message += `   🧩 _Incl. Parts: ${item.associatedParts.length} items_\n`;
                }
                message += `\n`;
            });
        }

        // B. Handle Product Items
        const products = invoice?.items || [];
        if (products.length > 0) {
            message += `📦 *PRODUCT ITEMS*\n`;
            products.forEach((item, idx) => {
                const name = item.itemName || item.name || 'Product';
                const qty = item.quantity || item.qty || 1;
                const price = item.price || 0;
                const total = item.totalPrice || (qty * price);

                message += `${idx + 1}. *${name}*\n`;
                message += `   ${qty} x ${formatCurrency(price)} = *${formatCurrency(total)}*\n`;
                if (item.warranty) message += `   🛡️ Warranty: ${item.warranty}\n`;
                if (item.barcode) message += `   🏷️ SN: \`\`\`${item.barcode}\`\`\`\n`;
                message += `\n`;
            });
        }

        message += `${WhatsAppFormatter._getDivider()}\n`;

        // C. Totals Section
        const subtotal = invoice?.subtotal || invoice?.total_amount || 0;
        message += `Subtotal:    ${formatCurrency(subtotal + (invoice?.discount || 0))}\n`;
        if (invoice?.discount > 0) message += `Discount:   -${formatCurrency(invoice.discount)}\n`;
        message += `*NET TOTAL:   ${formatCurrency(invoice?.total_amount || subtotal)}*\n`;

        const paid = invoice?.total_paid_amount || 0;
        const due = (invoice?.total_amount || 0) - paid;
        if (due > 0) message += `*DUE BALANCE: ${formatCurrency(due)}* ⚠️\n`;

        message += `${WhatsAppFormatter._getDivider()}\n`;

        message += `\n*Terms:*\n`;
        message += `• Inspect items thoroughly before leaving.\n`;
        message += `• No liability for after-sales physical damage.\n`;
        message += `• Refund or exchange is not entertained.\n`;

        message += WhatsAppFormatter._getFooter(company);

        return message;
    },

    /**
     * 2. REQUEST FOR QUOTATION (RFQ)
     */
    generateRFQ: (suggestion, company, user) => {
        const supplierName = suggestion.supplierName || "Supplier";
        const agentName = suggestion.agentName && suggestion.agentName !== 'Direct / Primary' ? suggestion.agentName : null;
        const brandName = (company?.company_name || 'Company').toUpperCase();
        const userName = user?.first_name ? `${user.first_name} ${user.last_name || ''}` : (user?.username || 'Procurement');

        let message = `*PROCUREMENT REQUEST | ${brandName}*\n`;
        message += `Ref: \`\`\`RFQ-${Date.now().toString().slice(-4)}\`\`\`\n`;
        message += `Date: ${new Date().toLocaleDateString()}\n`;
        message += `To: ${agentName ? agentName + ' (' + supplierName + ')' : supplierName}\n\n`;

        message += `We are evaluating supply requirements for the following items:\n\n`;

        message += `━━━━━━━━━━━━━━━━━━\n`;
        suggestion.items.forEach((item, index) => {
            const qty = item.targetQty || (item.stockTracking?.reorderPoint * 2 || 10);
            const variant = item.variantName ? ` [${item.variantName}]` : "";
            const unit = item.units || item.unit || 'pcs';

            message += `*${index + 1}. ${item.itemName}${variant}*\n`;
            message += `   Qty: *${qty} ${unit}*\n`;
            if (item.notes) message += `   _Note: ${item.notes}_\n`;
            message += `\n`;
        });
        message += `━━━━━━━━━━━━━━━━━━\n\n`;

        message += `Please provide your best pricing and earliest availability.\n\n`;
        message += `_Regards,_\n`;
        message += `*${userName}*\n`;
        message += `*${company?.company_name || ''}*\n`;
        message += `Tap to Reply: https://wa.me/${company?.phone_number}`;

        return message;
    },

    /**
     * 3. PURCHASE ORDER / TRADE-IN (PO)
     */
    generatePO: (purchase, company) => {
        const isTradeIn = purchase.purchase_type === "Trade-In";
        const vendorName = isTradeIn
            ? `Customer: ${purchase.customer?.first_name || 'Guest'}`
            : `Supplier: ${purchase.supplier?.business_name || purchase.supplierName || 'Vendor'}`;

        let message = isTradeIn ? `*TRADE-IN VOUCHER* 🔄\n` : `*PURCHASE ORDER (PO)* 📝\n`;
        message += `Ref: \`\`\`${purchase.referenceNumber || purchase.refNo}\`\`\`\n`;
        message += `Date: ${new Date(purchase.purchaseDate || Date.now()).toLocaleDateString()}\n`;
        message += `--------------------------------\n\n`;

        message += `${vendorName}\n`;
        message += `Hi, this is a confirmation for the following items:\n\n`;

        message += `━━━━━━━━━━━━━━━━━━\n`;
        const purchasedItems = purchase.purchasedItems || [];
        purchasedItems.forEach((item, idx) => {
            const name = item.item_id?.itemName || item.itemName || 'Item';
            const qty = item.purchaseQty || item.qty || 1;
            const cost = item.unitCost || item.price || 0;
            const total = item.total_price || (qty * cost);

            message += `*${idx + 1}. ${name}*\n`;
            message += `   ${qty} x ${formatCurrency(cost)} = *${formatCurrency(total)}*\n`;
            if (item.batch_number) message += `   - Batch: \`\`\`${item.batch_number}\`\`\`\n`;
            message += `\n`;
        });
        message += `━━━━━━━━━━━━━━━━━━\n\n`;

        message += `*GRAND TOTAL: ${formatCurrency(purchase.grand_total || purchase.total)}*\n`;
        message += `Status: ${purchase.payment_status || 'Pending'}\n`;
        message += `Payment: ${purchase.payment_type || 'N/A'}\n\n`;

        message += `*${company?.company_name || company?.companyName}*\n`;
        message += `*iShopMaster* | _Supply Chain Management_`;

        return message;
    },

    /**
     * 4. REPAIR JOB CARD (WIP/Intake)
     */
    generateJobCard: (job, company) => {
        let message = `*🛠️ REPAIR JOB CARD*\n`;
        message += `Job ID: \`\`\`${job.jobId || job.refNo}\`\`\`\n`;
        message += `Status: *${job.status || 'Received'}*\n`;
        message += `${WhatsAppFormatter._getSmallDivider()}\n\n`;

        message += `*CUSTOMER DETAILS*\n`;
        message += `Name: ${job.customerName}\n`;
        message += `Device: *${job.deviceModel}*\n\n`;

        message += `*REPORTED ISSUE*\n`;
        message += `> _${job.issue || 'General Service'}_\n\n`;

        message += `━━━━━━━━━━━━━━━━━━\n`;
        if (job.estimatedCost) message += `Est. Cost: *${formatCurrency(job.estimatedCost)}*\n`;
        message += `Ready By:  *${job.estimatedReadyDate || 'TBD'}*\n`;
        message += `━━━━━━━━━━━━━━━━━━\n\n`;

        message += `We will notify you once your device is ready for collection.\n`;
        message += WhatsAppFormatter._getFooter(company);

        return message;
    },

    /**
     * 5. WARRANTY STATUS LOOKUP
     */
    generateWarrantyStatus: (warranty, company) => {
        const isExpired = new Date(warranty.expiryDate) < new Date();
        const status = isExpired ? "❌ EXPIRED" : "✅ ACTIVE";

        let message = `*🛡️ WARRANTY VERIFICATION*\n`;
        message += `Status: *${status}*\n`;
        message += `${WhatsAppFormatter._getSmallDivider()}\n\n`;

        message += `*PRODUCT INFO*\n`;
        message += `Item:  ${warranty.itemName}\n`;
        message += `S/N:   \`\`\`${warranty.serialNumber}\`\`\`\n\n`;

        message += `*COVERAGE DETAILS*\n`;
        message += `Purchase Date: ${new Date(warranty.purchaseDate).toLocaleDateString()}\n`;
        message += `Expiry Date:   ${new Date(warranty.expiryDate).toLocaleDateString()}\n`;

        if (!isExpired && warranty.daysRemaining) {
            message += `Remaining:     *${warranty.daysRemaining} Days*\n`;
        }

        message += `\n━━━━━━━━━━━━━━━━━━\n`;
        message += `*Policy:* ${warranty.policyType || 'Standard Carry-in'}\n`;
        message += `━━━━━━━━━━━━━━━━━━\n\n`;

        message += `_Please present this message for warranty claims._\n`;
        message += WhatsAppFormatter._getFooter(company);

        return message;
    }
};

/**
 * Enterprise-Grade Phone Number Formatter for WhatsApp
 * Strictly follows WhatsApp's format: [country code][number] without any symbols.
 */
const formatWhatsAppNumber = (number, defaultCountry = "94") => {
    if (!number) return null;

    // 1. Convert to string and remove all non-numeric characters
    let cleaned = number.toString().replace(/[^0-9]/g, "");

    // 2. Remove all leading zeros from the entire string (standard cleaning)
    cleaned = cleaned.replace(/^0+/, "");

    // 3. Prevent Double Country Code & Handle Zero after Country Code (e.g. 94077...)
    if (cleaned.startsWith(defaultCountry)) {
        let rest = cleaned.substring(defaultCountry.length);
        // Remove any zeros that were accidentally left between country code and number
        if (rest.startsWith("0")) {
            rest = rest.replace(/^0+/, "");
        }
        return `${defaultCountry}${rest}`;
    }

    // 4. Handle Foreign/International numbers (assumed if length > 10)
    if (cleaned.length > 10) {
        return cleaned;
    }

    // 5. Add Default Country Code for local numbers (9-10 digits)
    if (cleaned.length === 9 || cleaned.length === 10) {
        if (cleaned.length === 10 && cleaned.startsWith("0")) {
            cleaned = cleaned.substring(1);
        }
        return `${defaultCountry}${cleaned}`;
    }

    // 6. Return as is if we can't determine (fallback)
    return cleaned;
};

/**
 * Universal execution helper to build URL and open window
 */
export const sendWhatsApp = (phoneNumber, message) => {
    if (!phoneNumber) {
        console.error("WhatsApp: No phone number provided");
        return;
    }

    const formattedNumber = formatWhatsAppNumber(phoneNumber);

    // Using the most robust URL format to ensure compatibility across devices
    const url = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank", "width=1200,height=600,noopener,noreferrer");
};